import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './LiveDraftExperience.css';

const DRAFT_ID = process.env.REACT_APP_LIVE_DRAFT_ID || '1312241670586122240';

const socketUrl = (api, draftId) => {
  const base = new URL(api || window.location.origin, window.location.origin);
  base.protocol = base.protocol === 'https:' ? 'wss:' : 'ws:';
  base.pathname = `${base.pathname.replace(/\/$/, '')}/api/drafts/ws/${encodeURIComponent(draftId)}`;
  base.search = '';
  return base.toString();
};

const mergeById = (items, next) => {
  const index = items.findIndex(item => item.id === next.id);
  if (index < 0) return [...items, next];
  return items.map((item, i) => (i === index ? next : item));
};

export default function LiveDraftExperience({ api = '', picks = [], currentUser, draftComplete = false }) {
  const [livePicks, setLivePicks] = useState(picks);
  const [grades, setGrades] = useState([]);
  const [comments, setComments] = useState([]);
  const [config, setConfig] = useState({ enabled: true, trash_talk_enabled: false });
  const [connected, setConnected] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [showTrashTalk, setShowTrashTalk] = useState(false);
  const [trashTalkOptIn, setTrashTalkOptIn] = useState(false);
  const [trashTalkPost, setTrashTalkPost] = useState(false);
  const [text, setText] = useState('');
  const [notice, setNotice] = useState('');
  const [muted, setMuted] = useState(() => {
    try { return JSON.parse(localStorage.getItem('draft.muted.v1') || '[]'); } catch { return []; }
  });
  const [moderation, setModeration] = useState([]);
  const retryRef = useRef(0);
  const reconnectRef = useRef(null);
  const isCommissioner = currentUser?.display_name === 'PhillyxCommish' || currentUser?.username === 'PhillyxCommish';

  const applyEvent = useCallback((event) => {
    if (event.type === 'snapshot') {
      setGrades(event.data.grades || []);
      setComments(event.data.comments || []);
      setConfig(event.data.config || {});
    } else if (event.type === 'grade.updated') {
      setGrades(previous => mergeById(previous, event.data));
    } else if (event.type === 'pick.created') {
      const metadata = event.data.metadata || {};
      const incoming = {
        ...event.data,
        name: event.data.name || `${metadata.first_name || ''} ${metadata.last_name || ''}`.trim() || 'Unknown player',
        position: event.data.position || metadata.position || '?',
        rookie_rank: event.data.rookie_rank || '—',
      };
      setLivePicks(previous => previous.some(pick => pick.pick_no === incoming.pick_no)
        ? previous.map(pick => pick.pick_no === incoming.pick_no ? { ...pick, ...incoming } : pick)
        : [...previous, incoming].sort((a, b) => a.pick_no - b.pick_no));
    } else if (event.type === 'comment.created') {
      setComments(previous => mergeById(previous, event.data));
    } else if (event.type === 'moderation.action') {
      setComments(previous => event.data.deleted
        ? previous.filter(comment => comment.id !== event.data.id)
        : mergeById(previous, event.data));
    }
  }, []);

  useEffect(() => {
    let active = true;
    let socket;
    const connect = () => {
      socket = new WebSocket(socketUrl(api, DRAFT_ID));
      socket.onopen = () => { retryRef.current = 0; setConnected(true); };
      socket.onmessage = message => {
        try {
          const event = JSON.parse(message.data);
          if (event.type === 'snapshot' && event.data?.config?.enabled === false) active = false;
          applyEvent(event);
        } catch { /* ignore malformed server frames */ }
      };
      socket.onclose = () => {
        setConnected(false);
        if (active) {
          const delay = Math.min(30000, 1000 * (2 ** retryRef.current++));
          reconnectRef.current = setTimeout(connect, delay);
        }
      };
    };
    connect();
    return () => {
      active = false;
      clearTimeout(reconnectRef.current);
      if (socket) socket.close();
    };
  }, [api, applyEvent]);

  useEffect(() => {
    setLivePicks(previous => {
      const merged = [...previous];
      picks.forEach(pick => {
        const index = merged.findIndex(item => item.pick_no === pick.pick_no);
        if (index >= 0) merged[index] = { ...merged[index], ...pick };
        else merged.push(pick);
      });
      return merged.sort((a, b) => a.pick_no - b.pick_no);
    });
  }, [picks]);

  const gradesByPick = useMemo(() => {
    const result = {};
    grades.forEach(grade => {
      const current = result[grade.pick_id];
      if (!current || (!grade.grader_id && current.grader_id)) result[grade.pick_id] = grade;
    });
    return result;
  }, [grades]);

  const visibleComments = comments.filter(comment =>
    !comment.deleted && !muted.includes(comment.author_id) && (showTrashTalk || !comment.is_trash_talk)
  );

  const persistMuted = next => {
    setMuted(next);
    localStorage.setItem('draft.muted.v1', JSON.stringify(next));
  };

  const postComment = async event => {
    event.preventDefault();
    setNotice('');
    const response = await fetch(`${api}/api/drafts/${DRAFT_ID}/comments`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        author_id: currentUser?.owner_id || currentUser?.user_id,
        author_name: currentUser?.display_name || currentUser?.username || 'Manager',
        text, is_trash_talk: trashTalkPost, trash_talk_opt_in: trashTalkOptIn,
      }),
    });
    const result = await response.json();
    if (!response.ok) { setNotice(result.detail || 'Could not post comment.'); return; }
    setText('');
    if (result.deleted) setNotice('Held for moderator review.');
  };

  const report = async comment => {
    const response = await fetch(`${api}/api/drafts/${DRAFT_ID}/comments/${comment.id}/flags`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ flagged_by: currentUser?.owner_id || currentUser?.user_id, reason: 'User report' }),
    });
    setNotice(response.ok ? 'Reported and hidden pending review.' : 'Could not report comment.');
  };

  const loadModeration = async () => {
    const pass = sessionStorage.getItem('hq.pass.v1');
    if (!pass) { setNotice('Authorize in Commissioner HQ first.'); return; }
    const response = await fetch(`${api}/api/drafts/${DRAFT_ID}/moderation`, { headers: { 'X-HQ-Pass': pass } });
    if (response.ok) setModeration((await response.json()).comments || []);
  };

  const moderate = async (comment, action) => {
    const pass = sessionStorage.getItem('hq.pass.v1');
    const response = await fetch(`${api}/api/drafts/${DRAFT_ID}/comments/${comment.id}/moderation`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json', 'X-HQ-Pass': pass },
      body: JSON.stringify({ action }),
    });
    if (response.ok) setModeration(previous => previous.filter(item => item.id !== comment.id));
  };

  if (!config.enabled) return null;

  return (
    <section className="live-draft" aria-label="Live draft grades and commentary">
      <div className="live-draft__heading">
        <div>
          <div className="live-draft__eyebrow">{draftComplete ? 'Post-draft room' : 'Live draft room'}</div>
          <h3>Grades & commentary</h3>
        </div>
        <span className={`live-draft__status ${connected ? 'is-live' : ''}`}>{connected ? 'Live' : 'Reconnecting'}</span>
      </div>

      <div className="live-draft__picks">
        {livePicks.length === 0 && <div className="live-draft__empty">Grades will appear as picks arrive.</div>}
        {livePicks.map(pick => {
          const pickId = `${DRAFT_ID}:${pick.pick_no}`;
          const grade = gradesByPick[pickId];
          return (
            <div className="live-pick" key={pickId}>
              <div className="live-pick__number">#{pick.pick_no}</div>
              <div className="live-pick__player"><strong>{pick.name}</strong><span>{pick.position} · Rookie #{pick.rookie_rank}</span></div>
              {grade ? (
                <div className="live-pick__grade" title={grade.rationale}>
                  <strong>{Math.round(grade.score)}</strong><span>{grade.rationale}</span>
                </div>
              ) : <div className="live-pick__pending">Grading…</div>}
            </div>
          );
        })}
      </div>

      <div className="commentary__heading">
        <button type="button" className="commentary__collapse" onClick={() => setCollapsed(value => !value)} aria-expanded={!collapsed}>
          {collapsed ? 'Expand' : 'Collapse'} commentary ({visibleComments.length})
        </button>
        {config.trash_talk_enabled ? (
          <label><input type="checkbox" checked={showTrashTalk} onChange={event => setShowTrashTalk(event.target.checked)} /> Show trash talk</label>
        ) : <span className="commentary__disabled">Trash talk disabled</span>}
      </div>

      {!collapsed && <>
        <div className="commentary__stream" aria-live="polite">
          {visibleComments.map(comment => (
            <article className={comment.is_trash_talk ? 'comment is-trash-talk' : 'comment'} key={comment.id}>
              <div><strong>{comment.author_name}</strong>{comment.is_trash_talk && <span className="comment__tag">Trash talk</span>}</div>
              <p>{comment.text}</p>
              <div className="comment__actions">
                <button type="button" onClick={() => report(comment)}>Report</button>
                {comment.author_id !== (currentUser?.owner_id || currentUser?.user_id) && (
                  <button type="button" onClick={() => persistMuted([...new Set([...muted, comment.author_id])])}>Mute user</button>
                )}
              </div>
            </article>
          ))}
          {visibleComments.length === 0 && <div className="live-draft__empty">No visible comments yet.</div>}
        </div>

        <form className="commentary__form" onSubmit={postComment}>
          <label htmlFor="draft-comment">Add commentary</label>
          <textarea id="draft-comment" maxLength="500" required value={text} onChange={event => setText(event.target.value)} />
          {config.trash_talk_enabled && <div className="commentary__consent">
            <label><input type="checkbox" checked={trashTalkOptIn} onChange={event => { setTrashTalkOptIn(event.target.checked); if (!event.target.checked) setTrashTalkPost(false); }} /> I opt in to trash-talk mode</label>
            <label><input type="checkbox" disabled={!trashTalkOptIn} checked={trashTalkPost} onChange={event => setTrashTalkPost(event.target.checked)} /> Post as trash talk</label>
          </div>}
          <div className="commentary__submit"><span>{notice}</span><button type="submit">Post</button></div>
        </form>

        {muted.length > 0 && <button type="button" className="commentary__reset" onClick={() => persistMuted([])}>Unmute all users</button>}
        {isCommissioner && <div className="moderation-tools">
          <button type="button" onClick={loadModeration}>Review flagged comments</button>
          {moderation.map(comment => <div className="moderation-item" key={comment.id}>
            <span><strong>{comment.author_name}:</strong> {comment.text} ({comment.flag_count} flag)</span>
            <button type="button" onClick={() => moderate(comment, 'approve')}>Approve</button>
            <button type="button" onClick={() => moderate(comment, 'delete')}>Keep deleted</button>
          </div>)}
        </div>}
      </>}
    </section>
  );
}
